import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Description as FileIcon,
  Download as DownloadIcon,
  Folder as FolderIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

interface InternalDocument {
  id: string;
  title: string;
  fileType: string;
  createdAt: string;
}

const ALLOWED_EXTENSIONS = '.pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf';

const Forms: React.FC = () => {
  const [documents, setDocuments] = useState<InternalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<InternalDocument | null>(null);
  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { token, hasPermission } = useAuth();
  const { t } = useTranslation();
  const canManage = hasPermission('forms.manage');

  useEffect(() => {
    fetchDocuments();
  }, [token]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl(API_ENDPOINTS.FORMS), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(t('forms.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc: InternalDocument) => {
    try {
      setError(null);
      const response = await fetch(getApiUrl(API_ENDPOINTS.FORM_DOWNLOAD(doc.id)), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i);
      const filename = filenameMatch
        ? decodeURIComponent(filenameMatch[1] || filenameMatch[2])
        : doc.title;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(t('forms.downloadError'));
    }
  };

  const openUploadDialog = () => {
    setEditingDoc(null);
    setTitle('');
    setSelectedFile(null);
    setDialogOpen(true);
  };

  const openEditDialog = (doc: InternalDocument) => {
    setEditingDoc(doc);
    setTitle(doc.title);
    setSelectedFile(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingDoc(null);
    setTitle('');
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError(t('forms.titleRequired'));
      return;
    }
    if (!editingDoc && !selectedFile) {
      setError(t('forms.fileRequired'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('title', title.trim());
      if (selectedFile) {
        formData.append('file', selectedFile);
        formData.append('originalFilename', selectedFile.name);
      }

      const url = editingDoc
        ? getApiUrl(API_ENDPOINTS.FORM_BY_ID(editingDoc.id))
        : getApiUrl(API_ENDPOINTS.FORMS);

      const response = await fetch(url, {
        method: editingDoc ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to save document');
      }

      setSuccess(editingDoc ? t('forms.updateSuccess') : t('forms.uploadSuccess'));
      closeDialog();
      await fetchDocuments();
    } catch (err: any) {
      console.error('Error saving document:', err);
      setError(err.message || t('forms.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setError(null);
      setSuccess(null);
      const response = await fetch(getApiUrl(API_ENDPOINTS.FORM_BY_ID(id)), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      setSuccess(t('forms.deleteSuccess'));
      setDeleteConfirmId(null);
      await fetchDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(t('forms.deleteError'));
    }
  };

  const getFileIcon = (fileType: string) => {
    const lower = fileType.toLowerCase();
    if (lower.includes('pdf')) return <FileIcon color="error" />;
    if (lower.includes('word')) return <FileIcon color="primary" />;
    if (lower.includes('excel')) return <FileIcon color="success" />;
    if (lower.includes('text')) return <FileIcon color="info" />;
    return <FileIcon />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('forms.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('forms.description')}
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openUploadDialog}>
            {t('forms.uploadDocument')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FolderIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">
            {t('forms.availableDocuments')}
          </Typography>
        </Box>

        {documents.length === 0 ? (
          <Box textAlign="center" py={4}>
            <InfoIcon color="action" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('forms.noDocuments')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('forms.noDocumentsDescription')}
            </Typography>
          </Box>
        ) : (
          <List>
            {documents.map((doc, index) => (
              <React.Fragment key={doc.id}>
                <ListItem disablePadding sx={{ pr: canManage ? 14 : 6 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getFileIcon(doc.fileType)}
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.title}
                    secondary={
                      <Chip
                        label={doc.fileType}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    }
                  />
                  <ListItemSecondaryAction>
                    {canManage && (
                      <>
                        <IconButton
                          edge="end"
                          aria-label={t('forms.edit')}
                          onClick={() => openEditDialog(doc)}
                          sx={{ mr: 0.5 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label={t('forms.delete')}
                          onClick={() => setDeleteConfirmId(doc.id)}
                          color="error"
                          sx={{ mr: 0.5 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                    <IconButton
                      edge="end"
                      aria-label={t('forms.download')}
                      onClick={() => handleDownload(doc)}
                      color="primary"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < documents.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDoc ? t('forms.editDocument') : t('forms.uploadDocument')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('forms.documentTitle')}
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button variant="outlined" component="label" fullWidth>
            {selectedFile ? selectedFile.name : t('forms.selectFile')}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept={ALLOWED_EXTENSIONS}
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
          </Button>
          {editingDoc && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('forms.replaceFileHint')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            {t('common.cancel', 'Atšaukti')}
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : t('common.save', 'Išsaugoti')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>{t('forms.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('forms.deleteConfirmMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>
            {t('common.cancel', 'Atšaukti')}
          </Button>
          <Button
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            color="error"
            variant="contained"
          >
            {t('forms.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Forms;
